import { get } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { requestPaginationSchema } from "@src/appV2/lib/api/types";
import { QuestionTag, Reaction } from "@src/appV2/redesign/Workplace/Review/types";
import { ShiftPeriod } from "@src/appV2/Shifts/Shift/types";
import { useQuery, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

/**
 * Backend sends uppercase shift period values ("AM", "PM", "NOC").
 * This is stored as-is in the schema, and converted to ShiftPeriod enum
 * when accessed via the exported type and helper.
 */

const backendShiftPeriodSchema = z.enum(["AM", "PM", "NOC"]);
type BackendShiftPeriod = z.infer<typeof backendShiftPeriodSchema>;

const BACKEND_TO_SHIFT_PERIOD: Record<BackendShiftPeriod, ShiftPeriod> = {
  AM: ShiftPeriod.AM,
  PM: ShiftPeriod.PM,
  NOC: ShiftPeriod.NOC,
};

/**
 * Converts backend shift period value to ShiftPeriod enum.
 */
export function toShiftPeriod(value: BackendShiftPeriod): ShiftPeriod {
  return BACKEND_TO_SHIFT_PERIOD[value];
}

export enum WorkerProfilePictureStatus {
  EXISTS_IN_FILE_STORAGE = "EXISTS_IN_FILE_STORAGE",
  DOES_NOT_EXIST = "DOES_NOT_EXIST",
}

export enum ReviewTag {
  FLEX = "FLEX",
  STAFF = "STAFF",
  SELF_REPORTED = "SELF_REPORTED",
}

export enum ReviewSource {
  CBH = "CBH",
  GOOGLE = "GOOGLE",
  YELP = "YELP",
  GLASSDOOR = "GLASSDOOR",
  INDEED = "INDEED",
}

const workerInfoSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  profilePictureStatus: z.nativeEnum(WorkerProfilePictureStatus).optional(),
  qualification: z.string().optional(),
  isAuthor: z.boolean().optional(),
});

export type WorkerInfoType = z.infer<typeof workerInfoSchema>;

const commentMediaSchema = z.object({
  id: z.string(),
  mediaType: z.string(),
  url: z.string(),
  mimeType: z.string().optional(),
  durationSeconds: z.number().optional(),
  fileSizeBytes: z.number().optional(),
  approvalStatus: z.string(),
});

export type CommentMedia = z.infer<typeof commentMediaSchema>;

const workplaceCommentAttributesSchema = z.object({
  text: z.string(),
  type: z.string().nullable().optional(),
  workerInfo: workerInfoSchema,
  workerReaction: z.nativeEnum(Reaction).nullable(),
  aggregates: z.object({
    totalLikes: z.number(),
    totalDislikes: z.number().optional(),
    totalReplies: z.number(),
  }),
  createdAt: z.string(),
  shiftId: z.string().optional(),
  shiftPeriod: backendShiftPeriodSchema.optional(),
  shiftWorkerQualification: z.string().optional(),
  patientsPerNurse: z.string().optional(),
  rating: z.number().min(1).max(5).optional(),
  media: z.array(commentMediaSchema).optional(),
  reviewTag: z.nativeEnum(ReviewTag).optional(),
  source: z.nativeEnum(ReviewSource).optional(),
  externalDetails: z
    .object({
      reviewerName: z.string().optional(),
      reviewerPhotoUrl: z.string().optional(),
      reviewUrl: z.string().optional(),
      publishedAt: z.string().optional(),
    })
    .optional(),
});

export type WorkplaceCommentAttributesType = z.infer<typeof workplaceCommentAttributesSchema>;

export const workplaceCommentSchema = z.object({
  id: z.string(),
  attributes: workplaceCommentAttributesSchema,
});

export type WorkplaceCommentType = z.infer<typeof workplaceCommentSchema>;

export const workplaceCommentsResponseSchema = z.object({
  data: z.array(workplaceCommentSchema),
  links: z.object({
    next: z.string().optional(),
  }),
});

export type WorkplaceCommentsResponse = z.infer<typeof workplaceCommentsResponseSchema>;

export const workplaceCommentsRequestSchema = z.object({
  filter: z
    .object({
      parentCommentId: z.string().optional(),
      questionId: z.string().optional(),
      questionTags: z.array(z.nativeEnum(QuestionTag)).optional(),
      excludeSelfComments: z.boolean().optional(),
      sort: z.enum(["-relevancyScore"]).optional(),
      searchText: z.string().max(255).optional(),
    })
    .optional(),
  page: requestPaginationSchema.optional(),
  viewContext: z.enum(["shifts", "placements"]).optional(),
});

export type WorkplaceCommentsRequest = z.infer<typeof workplaceCommentsRequestSchema>;

export type WorkplaceCommentsParams = WorkplaceCommentsRequest & {
  workplaceId: string;
};

export const WORKPLACE_COMMENTS_PAGE_SIZE = 20;

export function getWorkplaceCommentsUrl(workplaceId: string): string {
  return `${environmentConfig.REACT_APP_SHIFT_REVIEW_API_URL}/workplaces/${workplaceId}/comments`;
}

export function getWorkplaceCommentsQueryKey(params: WorkplaceCommentsParams) {
  const { workplaceId, ...workplaceCommentsRequestParams } = params;
  return [getWorkplaceCommentsUrl(workplaceId), workplaceCommentsRequestParams];
}

export function useWorkplaceComments(
  params: WorkplaceCommentsParams,
  options: UseQueryOptions<WorkplaceCommentType[], AxiosError> = {}
): UseQueryResult<WorkplaceCommentType[]> {
  const { workplaceId, ...workplaceCommentsRequestParams } = params;
  return useQuery({
    queryKey: getWorkplaceCommentsQueryKey(params),
    queryFn: async () => {
      const response = await get({
        url: getWorkplaceCommentsUrl(workplaceId),
        queryParams: workplaceCommentsRequestParams,
        responseSchema: workplaceCommentsResponseSchema,
        requestSchema: workplaceCommentsRequestSchema,
      });

      return response.data.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_REVIEW_COMMENTS_FAILURE,
    },
    ...options,
  });
}
